import React, { createContext, useContext, useState, useCallback, forwardRef } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import { cn } from '../../lib/utils';

/* ---------- Context ---------- */
const HoverSliderContext = createContext(undefined);

function useHoverSliderContext() {
  const ctx = useContext(HoverSliderContext);
  if (!ctx) throw new Error('useHoverSliderContext must be inside HoverSlider');
  return ctx;
}

/* ---------- Helpers ---------- */
function splitText(text) {
  const words = text.split(' ').map((w) => w.concat(' '));
  return { characters: words.map((w) => w.split('')).flat() };
}

/* ---------- Root ---------- */
export const HoverSlider = forwardRef(function HoverSlider({ children, className, ...props }, ref) {
  const [activeSlide, setActiveSlide] = useState(0);
  const changeSlide = useCallback((i) => setActiveSlide(i), []);
  return (
    <HoverSliderContext.Provider value={{ activeSlide, changeSlide }}>
      <div className={className} {...props}>{children}</div>
    </HoverSliderContext.Provider>
  );
});

/* ---------- Text with stagger ---------- */
export const TextStaggerHover = forwardRef(function TextStaggerHover({ text, index, className, ...props }, ref) {
  const { activeSlide, changeSlide } = useHoverSliderContext();
  const { characters } = splitText(text);
  const isActive = activeSlide === index;

  return (
    <span
      className={cn('relative inline-block origin-bottom overflow-hidden', className)}
      ref={ref}
      onMouseEnter={() => changeSlide(index)}
      {...props}
    >
      {characters.map((char, i) => (
        <span key={`${char}-${i}`} className="relative inline-block overflow-hidden">
          <MotionConfig
            transition={{ delay: i * 0.025, duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <motion.span
              className="inline-block opacity-20"
              initial={{ y: '0%' }}
              animate={isActive ? { y: '-110%' } : { y: '0%' }}
            >
              {char}
              {char === ' ' && i < characters.length - 1 && <>&nbsp;</>}
            </motion.span>
            <motion.span
              className="absolute left-0 top-0 inline-block opacity-100"
              initial={{ y: '110%' }}
              animate={isActive ? { y: '0%' } : { y: '110%' }}
            >
              {char}
            </motion.span>
          </MotionConfig>
        </span>
      ))}
    </span>
  );
});

/* ---------- Image wrapper (grid stack) ---------- */
export const HoverSliderImageWrap = forwardRef(function HoverSliderImageWrap({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'grid overflow-hidden [&>*]:col-start-1 [&>*]:col-end-1 [&>*]:row-start-1 [&>*]:row-end-1 [&>*]:size-full',
        className
      )}
      {...props}
    />
  );
});

/* ---------- Image slide ---------- */
const clipPathVariants = {
  visible: { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },
  hidden: { clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0px)' },
};

export const HoverSliderImage = forwardRef(function HoverSliderImage({ index, imageUrl, className, ...props }, ref) {
  const { activeSlide } = useHoverSliderContext();
  return (
    <motion.img
      className={cn('inline-block align-middle', className)}
      transition={{ ease: [0.33, 1, 0.68, 1], duration: 0.8 }}
      variants={clipPathVariants}
      animate={activeSlide === index ? 'visible' : 'hidden'}
      ref={ref}
      src={imageUrl}
      {...props}
    />
  );
});
